import type {
  AspectRatio,
  CharacterAsset,
  CharacterStyle,
  SceneSettings,
  ShapeStyle,
  StoryCell,
  StoryElement,
  StoryboardProject,
  TextStyle,
} from '../types';

export const uid = (prefix = 'id'): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const nowIso = (): string => new Date().toISOString();

export const getCanvasSize = (aspect: AspectRatio): { width: number; height: number } => {
  switch (aspect) {
    case 'landscape':
      return { width: 1200, height: 900 };
    case 'square':
      return { width: 1000, height: 1000 };
    case 'portrait':
      return { width: 675, height: 1200 };
    case 'cinematic':
    default:
      return { width: 1600, height: 900 };
  }
};

export const defaultTextStyle = (): TextStyle => ({
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  fontSize: 54,
  fontWeight: 700,
  italic: false,
  underline: false,
  align: 'center',
  fill: '#27243c',
  background: 'transparent',
  border: 'transparent',
  borderWidth: 0,
  radius: 18,
  padding: 22,
  lineHeight: 1.16,
});

export const defaultScene = (assetId = 'studio-loft'): SceneSettings => ({
  assetId,
  time: 'day',
  weather: 'clear',
  intensity: 0.7,
});

export const createCell = (index = 0, sceneId = 'studio-loft'): StoryCell => ({
  id: uid('cell'),
  title: `Scene ${index + 1}`,
  description: '',
  durationMs: 2200,
  scene: defaultScene(sceneId),
  elements: [],
});

export const createBlankProject = (title = 'Untitled Storyboard'): StoryboardProject => {
  const now = nowIso();
  return {
    schemaVersion: 1,
    id: uid('project'),
    title,
    createdAt: now,
    updatedAt: now,
    layout: {
      columns: 3,
      aspectRatio: 'cinematic',
      showTitles: true,
      showDescriptions: false,
      snapToGrid: true,
      gridSize: 10,
      background: '#f7f5fc',
    },
    cells: [createCell(0)],
  };
};

export const normalizeZIndices = (elements: StoryElement[]): StoryElement[] =>
  [...elements]
    .sort((a, b) => a.zIndex - b.zIndex)
    .map((element, index) => ({ ...element, zIndex: index }));

export const createCharacterElement = (
  asset: CharacterAsset,
  x: number,
  y: number,
  overrides?: Partial<CharacterStyle>,
): StoryElement => ({
  id: uid('el'),
  kind: 'character',
  name: asset.name,
  assetId: asset.id,
  x,
  y,
  width: 220,
  height: 390,
  rotation: 0,
  opacity: 1,
  flipX: false,
  flipY: false,
  locked: false,
  hidden: false,
  zIndex: 10,
  character: { ...asset.defaultStyle, ...overrides },
});

export const createTextElement = (
  text = 'Add your words',
  x = 400,
  y = 250,
  width = 500,
  height = 150,
): StoryElement => ({
  id: uid('el'),
  kind: 'text',
  name: 'Text',
  x,
  y,
  width,
  height,
  rotation: 0,
  opacity: 1,
  flipX: false,
  flipY: false,
  locked: false,
  hidden: false,
  zIndex: 20,
  text,
  textStyle: defaultTextStyle(),
});

export const createBubbleElement = (
  style: StoryElement['bubbleStyle'] = 'speech',
  text = 'Say something…',
  x = 440,
  y = 120,
): StoryElement => ({
  id: uid('el'),
  kind: 'bubble',
  name: style === 'caption' ? 'Caption' : 'Speech bubble',
  x,
  y,
  width: 520,
  height: 230,
  rotation: 0,
  opacity: 1,
  flipX: false,
  flipY: false,
  locked: false,
  hidden: false,
  zIndex: 30,
  text,
  bubbleStyle: style,
  textStyle: {
    ...defaultTextStyle(),
    fontSize: 46,
    background: style === 'caption' ? '#fff2a8' : '#ffffff',
    border: '#27243c',
    borderWidth: style === 'whisper' ? 3 : 5,
    radius: style === 'caption' ? 12 : 52,
    padding: 28,
  },
});

export const createShapeElement = (shape: ShapeStyle, x = 500, y = 300): StoryElement => ({
  id: uid('el'),
  kind: 'shape',
  name: shape[0].toUpperCase() + shape.slice(1),
  x,
  y,
  width: shape === 'line' || shape === 'arrow' ? 420 : 260,
  height: shape === 'line' || shape === 'arrow' ? 100 : 220,
  rotation: 0,
  opacity: 1,
  flipX: false,
  flipY: false,
  locked: false,
  hidden: false,
  zIndex: 15,
  shapeStyle: shape,
  appearance: {
    fill: shape === 'line' || shape === 'arrow' ? 'transparent' : '#7b6cff',
    stroke: '#27243c',
    strokeWidth: 8,
  },
});


export const createPropElement = (
  assetId: string,
  name: string,
  aspect: number,
  x = 500,
  y = 350,
  width = 220,
): StoryElement => ({
  id: uid('el'),
  kind: 'prop',
  name,
  assetId,
  x,
  y,
  width,
  height: width / aspect,
  rotation: 0,
  opacity: 1,
  flipX: false,
  flipY: false,
  locked: false,
  hidden: false,
  zIndex: 12,
});

export const createImageElement = (
  dataUrl: string,
  name: string,
  width: number,
  height: number,
  alt = '',
): StoryElement => {
  const maxWidth = 700;
  const scale = width > maxWidth ? maxWidth / width : 1;
  return {
    id: uid('el'),
    kind: 'image',
    name,
    x: 160,
    y: 100,
    width: Math.max(100, width * scale),
    height: Math.max(100, height * scale),
    rotation: 0,
    opacity: 1,
    flipX: false,
    flipY: false,
    locked: false,
    hidden: false,
    zIndex: 18,
    dataUrl,
    alt,
  };
};

export const cloneProjectWithNewId = (project: StoryboardProject, title?: string): StoryboardProject => {
  const now = nowIso();
  const next = structuredClone(project);
  next.id = uid('project');
  next.title = title ?? `${project.title} copy`;
  next.createdAt = now;
  next.updatedAt = now;
  next.cells = next.cells.map((cell, index) => ({
    ...cell,
    id: uid('cell'),
    title: cell.title || `Scene ${index + 1}`,
    elements: cell.elements.map((element) => ({ ...element, id: uid('el') })),
  }));
  return next;
};
